import { useEffect, useState } from 'react';
import { servicesAPI } from '../api/services';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, Sparkles, Clock, ExternalLink } from 'lucide-react';
import Loader from '../components/common/Loader';
import SeoMeta from '../components/SeoMeta';

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Développement web, marketing et applications React",
  provider: {
    "@type": "Organization",
    name: "Dev-Tech",
    url: "https://devtechpro.fr",
    logo: "https://devtechpro.fr/logo-og.png"
  },
  areaServed: "FR",
  audience: "Entreprises recherchant des solutions digitales innovantes"
};

export default function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      const response = await servicesAPI.getAll();
      setServices(response.data);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loader />;

  const getGradientFromColor = (color) => {
    const colorMap = {
      blue: 'from-blue-500 to-indigo-600',
      purple: 'from-purple-500 to-violet-600',
      green: 'from-emerald-500 to-green-600',
      pink: 'from-pink-500 to-rose-600',
      orange: 'from-amber-500 to-orange-600',
      teal: 'from-teal-500 to-cyan-600',
      default: 'from-blue-500 to-purple-600'
    };
    return colorMap[color] || colorMap.default;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-black to-gray-900 pt-20"> {/* Ajout de pt-20 */}
      <SeoMeta
        title="Offre de services numériques"
        description="Partenaire Dev-Tech: services web, applications React, automatisations et marketing pour accélérer la croissance digitale."
        canonical="https://devtechpro.fr/services"
        jsonLd={jsonLd}
      />
      {/* Hero Section */}
      <section className="relative pt-24 pb-20 overflow-hidden"> {/* Changement de pt-32 à pt-24 */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-indigo-900/10 to-purple-900/20" />
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent" />
        
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 mb-6 backdrop-blur-sm">
              <Sparkles className="w-4 h-4 text-blue-400" />
              <span className="text-sm font-semibold text-blue-300">EXPERTISES PREMIUM</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Solutions Digitales <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">sur Mesure</span>
            </h1>
            
            <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-8 leading-relaxed">
              Des services complets et innovants pour transformer votre présence digitale 
              et accélérer la croissance de votre entreprise.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="pb-32">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
            {services.map((service) => (
              <div key={service._id} className="group relative isolate">
                {/* Card Background - z-index très bas */}
                <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl border border-gray-700 group-hover:border-blue-500/50 transition-colors duration-300 -z-10" />
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl -z-10" />
                
                {/* Header avec gradient */}
                <div className={`relative ${getGradientFromColor(service.color)} p-8 rounded-t-2xl overflow-hidden`}>
                  <div className="absolute inset-0 bg-gradient-to-r from-white/10 via-transparent to-white/5 opacity-30" />
                  
                  <div className="relative z-10">
                    <h3 className="text-2xl font-bold text-white mb-2">{service.title}</h3>
                    {service.priceStartingFrom && (
                      <div className="text-white/90">
                        À partir de <span className="text-3xl font-black text-white ml-2">{service.priceStartingFrom}€</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Card Content */}
                <div className="relative p-8">
                  <p className="text-gray-300 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  
                  {service.deliveryTime && (
                    <div className="flex items-center gap-3 text-gray-400 mb-6 p-4 bg-gray-800/50 rounded-xl border border-gray-700">
                      <Clock className="w-5 h-5" />
                      <span className="text-sm font-medium">Délai moyen: {service.deliveryTime}</span>
                    </div>
                  )}
                  
                  {service.features && service.features.length > 0 && (
                    <div className="mb-8">
                      <h4 className="text-lg font-semibold text-white mb-4">Ce qui est inclus :</h4>
                      <ul className="space-y-3">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center gap-3 text-gray-300 border-b border-gray-800/50 pb-3 last:border-0">
                            <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                            <span className="text-sm">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* CONTAINER DES BOUTONS - z-index élevé */}
                  <div className="relative z-30 flex flex-col gap-4 pt-6 border-t border-gray-800">
                    <Link 
                      to={`/services/${service._id}`}
                      className="relative z-40 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-200 hover:gap-3"
                    >
                      <span>Voir les détails</span>
                      <ExternalLink className="w-4 h-4" />
                    </Link>
                    <Link 
                      to="/contact"
                      className="relative z-40 px-6 py-3 bg-gray-800/50 text-gray-300 border border-gray-700 rounded-xl font-medium hover:bg-gray-700/50 hover:text-white transition-colors duration-200 text-center"
                    >
                      Demander un devis personnalisé
                    </Link>
                  </div>
                </div>

                {/* Glow Effect - z-index très bas */}
                <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500/20 to-purple-500/20 opacity-0 group-hover:opacity-100 blur transition-opacity duration-300 rounded-2xl -z-20" />
              </div>
            ))}
          </div>

          {/* CTA Section */}
          <div className="relative max-w-4xl mx-auto">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-3xl blur-xl" />
            
            <div className="relative bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm border border-gray-700 rounded-3xl p-12 text-center">
              <div className="max-w-2xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                  Un projet spécifique en tête ?
                </h2>
                <p className="text-gray-300 text-lg mb-8 leading-relaxed">
                  Nos experts sont disponibles pour discuter de vos besoins uniques 
                  et créer une solution sur mesure pour votre entreprise.
                </p>
                <Link 
                  to="/contact"
                  className="relative z-10 inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-white to-gray-100 text-gray-900 font-bold rounded-xl hover:shadow-2xl hover:shadow-white/20 transition-all duration-200 hover:gap-4"
                >
                  Discuter avec un expert
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}